import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const CloudinaryConfigForm = () => {
    const [config, setConfig] = useState({ cloud_name: '', upload_preset: '' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const docRef = doc(db, 'config', 'cloudinary');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setConfig(docSnap.data());
                } else {
                    setConfig({ cloud_name: '', upload_preset: '' });
                }
            } catch (err) {
                console.error("Error fetching Cloudinary config:", err);
                setError('Nepodařilo se načíst konfiguraci Cloudinary.');
            } finally {
                setLoading(false);
            }
        };
        fetchConfig();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setConfig(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const docRef = doc(db, 'config', 'cloudinary');
            await setDoc(docRef, config, { merge: true });
            setSuccess('Konfigurace byla úspěšně uložena.');
        } catch (err) {
            console.error("Error saving Cloudinary config:", err);
            const code = err.code || 'unknown';
            const msg = err.message || 'Neznámá chyba';
            const hint = code === 'permission-denied'
              ? ' – Nedostatečná oprávnění ve Firestore. Přihlaste se nebo upravte rules.'
              : '';
            setError(`Chyba při ukladaní konfigurace: ${msg}${hint}`);
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        if (!config.cloud_name || !config.upload_preset) {
            setTestResult({ success: false, message: 'Cloud Name a Upload Preset musí být vyplněné.' });
            return;
        }
        setTestResult({ success: null, message: 'Test připojení...' });

        // A 1x1 transparent GIF
        const testPixel = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

        const formData = new FormData();
        formData.append('file', testPixel);
        formData.append('upload_preset', config.upload_preset);

        try {
            const response = await fetch(`https://api.cloudinary.com/v1_1/${config.cloud_name}/image/upload`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok && data.secure_url) {
                setTestResult({ success: true, message: 'Připojení je úspěšné!' });
            } else {
                throw new Error(data.error?.message || 'Neznáma chyba');
            }
        } catch (err) {
            console.error('Cloudinary test failed:', err);
            setTestResult({ success: false, message: `Test zlyhal: ${err.message}` });
        }
    };

    if (loading) return <p>Načítavam konfiguráciu...</p>;

    return (
        <div className="p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-6">Cloudinary konfigurace</h2>
            {error && <p className="text-red-500 bg-red-100 p-3 rounded mb-4">{error}</p>}
            {success && <p className="text-green-600 bg-green-100 p-3 rounded mb-4">{success}</p>}
            <form onSubmit={handleSave} className="space-y-6">
                <div>
                    <label htmlFor="cloud_name" className="block text-sm font-medium text-gray-700 mb-1">
                        Cloud Name
                    </label>
                    <input
                        type="text"
                        name="cloud_name"
                        id="cloud_name"
                        value={config.cloud_name}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="vas_cloud_name"
                    />
                </div>
                <div>
                    <label htmlFor="upload_preset" className="block text-sm font-medium text-gray-700 mb-1">
                        Upload Preset
                    </label>
                    <input
                        type="text"
                        name="upload_preset"
                        id="upload_preset"
                        value={config.upload_preset}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="vas_upload_preset"
                    />
                </div>
                <div className="flex items-center space-x-4">
                    <button
                        type="submit"
                        className="bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition duration-300 disabled:opacity-50"
                        disabled={saving}
                    >
                        {saving ? 'Ukladám...' : 'Uložit konfiguraci'}
                    </button>
                    <button
                        type="button"
                        onClick={handleTestConnection}
                        className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition duration-300"
                    >
                        Test připojení
                    </button>
                </div>
            </form>
            {testResult && (
                <div className={`mt-4 p-3 rounded text-sm ${testResult.success ? 'bg-green-100 text-green-800' : testResult.success === false ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                    {testResult.message}
                </div>
            )}
        </div>
    );
};

export default CloudinaryConfigForm;
